import { useState, useEffect, useCallback } from 'react';
import { mastersAPI } from '../../utils/api';
import { useAuth } from '../../hooks/useAuth';

import { useToast, apiError, useConfirm } from '../../components/Toast';
import { clearMastersCache } from '../../hooks/useMasters';
import { Plus, SquarePen, Trash2, X } from 'lucide-react';

const CATEGORY_META = {
  // Tasks
  task_status:     { label:'Task Status',           module:'Tasks',        icon:'✅', color:'#10b981' },
  priority:        { label:'Priority Levels',        module:'Shared',       icon:'🎯', color:'#2E539F' },
  work_type:       { label:'Task Work Types',        module:'Tasks',        icon:'⚙️', color:'#2E539F' },
  task_type:       { label:'Task Types',             module:'Tasks',        icon:'📌', color:'#6366f1' },
  // Activities
  activity_status: { label:'Activity Status',        module:'Activities',   icon:'🔄', color:'#5CC3BC' },
  activity_type:   { label:'Activity Types',         module:'Activities',   icon:'⚡', color:'#5CC3BC' },
  // Dev Hub — Bugs
  bug_status:      { label:'Bug Status',             module:'Dev Hub',      icon:'🐛', color:'#f97316' },
  bug_severity:    { label:'Bug Severity',           module:'Dev Hub',      icon:'⚠️', color:'#ef4444' },
  bug_issue_type:  { label:'Bug Issue Types',        module:'Dev Hub',      icon:'🏷️', color:'#8b5cf6' },
  bug_environment: { label:'Bug Environment',        module:'Dev Hub',      icon:'🖥️', color:'#0891b2' },
  // Dev Hub — Change Requests
  cr_status:       { label:'CR Approval Status',    module:'Dev Hub',      icon:'📋', color:'#0891b2' },
  cr_impact:       { label:'CR Impact / Priority',  module:'Dev Hub',      icon:'💥', color:'#f59e0b' },
  cr_request_type: { label:'CR Request Types',      module:'Dev Hub',      icon:'📝', color:'#6366f1' },
  billing_type:    { label:'Billing Types',          module:'Dev Hub',      icon:'💰', color:'#2E539F' },
  // Projects
  project_status:  { label:'Project Status',         module:'Projects',     icon:'📁', color:'#2E539F' },
  project_type:    { label:'Project Types',          module:'Projects',     icon:'🗂️', color:'#2E539F' },
  rag_status:      { label:'RAG Health Status',      module:'Projects',     icon:'🚦', color:'#10b981' },
  risk_level:      { label:'Risk Levels',            module:'Risk Register',icon:'🔰', color:'#f97316' },
  risk_category:   { label:'Risk Categories',        module:'Risk Register',icon:'🗂️', color:'#8b5cf6' },
  risk_status:     { label:'Risk Statuses',          module:'Risk Register',icon:'📊', color:'#2E539F' },
  milestone_status:{ label:'Milestone Statuses',     module:'Milestones',   icon:'🏁', color:'#2E539F' },
  // Clients & Training
  client_type:     { label:'Client Types',           module:'Clients',      icon:'🏢', color:'#2E539F' },
  client_status:   { label:'Client Status',          module:'Clients',      icon:'📊', color:'#10b981' },
  training_type:   { label:'Training Session Types', module:'Training',     icon:'🎓', color:'#5CC3BC' },
  training_status: { label:'Training Status',        module:'Training',     icon:'📚', color:'#5CC3BC' },
};

const MODULES = ['All', ...new Set(Object.values(CATEGORY_META).map(m => m.module))];

const XIcon = () => <X size={14} />;
const PlusIcon = () => <Plus size={14} />;
const EditIcon = () => <SquarePen size={13} />;
const TrashIcon = () => <Trash2 size={13} />;

export default function MastersPage() {
  const confirm = useConfirm();
  const toast = useToast();
  const { user } = useAuth();
  const [data,        setData]        = useState({});
  const [error, setError] = useState('');
  const [loading,     setLoading]     = useState(true);
  const [moduleFilter,setModuleFilter]= useState('All');
  // One category at a time. All 26 expanded at once made a page nobody could
  // scan — the list you wanted was always several screens down.
  const [openCat, setOpenCat] = useState(null);
  const [search,      setSearch]      = useState('');
  const [selected,    setSelected]    = useState(null); // { category, item }
  const [addModal,    setAddModal]    = useState(null); // category
  const [addForm,     setAddForm]     = useState({ label:'', value:'', sort_order:'0' });
  const [editModal,   setEditModal]   = useState(null); // item
  const [editForm,    setEditForm]    = useState({});
  const [saving,      setSaving]      = useState(false);

  const isAdmin = ['Admin','CEO'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await mastersAPI.getAll(); setData(r.data || {}); setError(''); }
    catch(e) { setError(e.response?.data?.message || 'Failed to load picklists. Check server connection.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!addForm.label.trim() || !addForm.value.trim()) return toast.warning('Label and value are required');
    setSaving(true);
    try {
      await mastersAPI.create({ category: addModal, ...addForm, sort_order: parseInt(addForm.sort_order)||0 });
      toast.success('Item created');
      clearMastersCache();   // forms re-read the list on their next render
      setAddModal(null); setAddForm({ label:'', value:'', sort_order:'0' }); load();
    } catch(e) { toast.error(apiError(e, 'Failed to add item')); }
    finally { setSaving(false); }
  };

  const handleEdit = async () => {
    setSaving(true);
    try {
      await mastersAPI.update(editModal.id, editForm);
      toast.success('Item updated');
      clearMastersCache();   // forms re-read the list on their next render
      setEditModal(null); load();
    } catch(e) { toast.error(apiError(e, 'Failed to update item')); }
    finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!await confirm(`Delete "${item.label}"? This cannot be undone.`)) return;
    try { await mastersAPI.delete(item.id);
    toast.success('Item deleted');
      clearMastersCache();   // forms re-read the list on their next render
      load(); }
    catch(e) { toast.error(apiError(e, 'Failed to delete item')); }
  };

  const filteredCategories = Object.entries(data).filter(([cat]) => {
    const meta = CATEGORY_META[cat];
    const matchModule = moduleFilter === 'All' || meta?.module === moduleFilter;
    const matchSearch = !search ||
      meta?.label?.toLowerCase().includes(search.toLowerCase()) ||
      cat.toLowerCase().includes(search.toLowerCase());
    return matchModule && matchSearch;
  });

  return (
    <div className="space-y-5">
      {error && (
        <div className="pt-2">
          <div className="p-3 rounded-xl text-sm mb-2" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444', border:'1px solid rgba(239,68,68,.2)' }}>
            ⚠ {error} <button className="ml-2 underline text-xs" onClick={()=>setError('')}>Dismiss</button>
          </div>
        </div>
      )}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title">Masters — Picklist Management</h1>
          <p className="text-sm mt-0.5" style={{ color:'var(--text-secondary)' }}>
            Manage all dropdown options used across the system. Changes reflect immediately in all modules.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <input className="input pl-8 text-sm w-56" placeholder="Search categories..."
            value={search} onChange={e => setSearch(e.target.value)}/>
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm" style={{ color:'var(--text-secondary)' }}>🔍</span>
        </div>
        <select className="select text-sm w-44" value={moduleFilter} onChange={e => setModuleFilter(e.target.value)}>
          {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <span className="text-xs" style={{ color:'var(--text-secondary)' }}>
          {filteredCategories.length} categor{filteredCategories.length !== 1 ? 'ies' : 'y'}
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>
      ) : (
        <div className="space-y-4">
          {/* Tiles until one is chosen. The card below is unchanged — it is
              simply rendered for one category instead of all of them. */}
          {!openCat && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {filteredCategories.map(([cat, items]) => {
                const meta = CATEGORY_META[cat] || { label: cat, module:'Other', icon:'📋', color:'#94a3b8' };
                const fixed = items.some(i => i.is_system);
                return (
                  <button key={cat} onClick={() => setOpenCat(cat)}
                    className="card text-left p-4 hover:shadow-md transition-shadow"
                    style={{ borderLeft: `3px solid ${meta.color}` }}>
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-xl">{meta.icon}</span>
                      <span className="text-lg font-bold" style={{ color:'var(--text)' }}>{items.length}</span>
                    </div>
                    <p className="font-semibold text-sm leading-tight" style={{ color:'var(--text)' }}>
                      {meta.label}
                    </p>
                    <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
                      {meta.module}{fixed ? ' · fixed list' : ''}
                    </p>
                  </button>
                );
              })}
            </div>
          )}

          {openCat && (
            <button onClick={() => setOpenCat(null)}
              className="text-sm font-semibold" style={{ color:'#2E539F' }}>
              ← All categories
            </button>
          )}

          {(openCat ? filteredCategories.filter(([c]) => c === openCat) : []).map(([cat, items]) => {
            const meta = CATEGORY_META[cat] || { label: cat, module: 'Other', icon: '📋', color:'#94a3b8' };
            return (
              <div key={cat} className="card overflow-hidden p-0">
                {/* Category header */}
                <div className="flex items-center justify-between px-5 py-3 border-b"
                  style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{meta.icon}</span>
                    <div>
                      <h3 className="font-semibold text-sm" style={{ color:'var(--text)' }}>{meta.label}</h3>
                      <p className="text-xs" style={{ color:'var(--text-secondary)' }}>
                        Module: <span className="font-medium" style={{ color:meta.color }}>{meta.module}</span>
                        &nbsp;· {items.length} item{items.length !== 1 ? 's' : ''}
                        &nbsp;· Key: <code className="text-xs">{cat}</code>
                      </p>
                    </div>
                  </div>
                  {isAdmin && (
                    <button onClick={() => { setAddModal(cat); setAddForm({ label:'', value:'', sort_order: String(items.length) }); }}
                      className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5">
                      <PlusIcon/> Add Item
                    </button>
                  )}
                </div>

                {/* Items table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b" style={{ borderColor:'var(--border)' }}>
                        {['Label','Value','Sort','Type','Actions'].map(h => (
                          <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider"
                            style={{ color:'var(--text-secondary)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => (
                        <tr key={item.id}
                          className="border-b last:border-0 hover:bg-[var(--surface-alt)] transition-colors"
                          style={{ borderColor:'var(--border)' }}>
                          <td className="px-4 py-2.5 font-medium" style={{ color:'var(--text)' }}>
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background:meta.color }}/>
                              {item.label}
                            </div>
                          </td>
                          <td className="px-4 py-2.5">
                            <code className="text-xs px-1.5 py-0.5 rounded" style={{ background:'var(--surface-alt)', color:'var(--text-secondary)' }}>
                              {item.value}
                            </code>
                          </td>
                          <td className="px-4 py-2.5 text-xs" style={{ color:'var(--text-secondary)' }}>
                            {item.sort_order}
                          </td>
                          <td className="px-4 py-2.5">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.is_system ? 'bg-brand-100 text-brand-700' : 'bg-emerald-100 text-emerald-700'}`}>
                              {item.is_system ? '🔒 System' : '✏️ Custom'}
                            </span>
                          </td>
                          <td className="px-4 py-2.5">
                            {isAdmin && !item.is_system ? (
                              <div className="flex items-center gap-1">
                                <button onClick={() => { setEditModal(item); setEditForm({ label:item.label, value:item.value, sort_order:item.sort_order, is_active:item.is_active }); }}
                                  className="p-1.5 rounded hover:bg-[var(--surface-alt)] transition-colors" style={{ color:'var(--text-secondary)' }}
                                  title="Edit"><EditIcon/></button>
                                <button onClick={() => handleDelete(item)}
                                  className="p-1.5 rounded text-red-400 hover:bg-red-50 transition-colors"
                                  title="Delete"><TrashIcon/></button>
                              </div>
                            ) : (
                              <span className="text-xs" style={{ color:'var(--text-secondary)' }}>
                                {item.is_system ? 'Read-only' : '—'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
          {filteredCategories.length === 0 && (
            <div className="card text-center py-16">
              <p className="text-3xl mb-2">📋</p>
              <p className="font-medium" style={{ color:'var(--text)' }}>No categories found</p>
              <p className="text-sm mt-1" style={{ color:'var(--text-secondary)' }}>Try adjusting your filters</p>
            </div>
          )}
        </div>
      )}

      {/* Add Item Modal */}
      {addModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setAddModal(null)}/>
          <div className="relative bg-[var(--surface)] rounded-2xl shadow-2xl w-full max-w-md z-10"
            style={{ border:'1px solid var(--border)' }}>
            <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor:'var(--border)' }}>
              <h3 className="font-bold" style={{ color:'var(--text)' }}>
                Add to {CATEGORY_META[addModal]?.label || addModal}
              </h3>
              <button onClick={() => setAddModal(null)} className="p-1 rounded hover:bg-[var(--surface-alt)]" style={{ color:'var(--text-secondary)' }}><XIcon/></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="label">Label <span className="text-red-500">*</span></label>
                <input className="input" value={addForm.label} onChange={e => setAddForm(f=>({...f, label:e.target.value}))}
                  placeholder="Display name shown in dropdowns"/>
              </div>
              <div>
                <label className="label">Value <span className="text-red-500">*</span></label>
                <input className="input" value={addForm.value} onChange={e => setAddForm(f=>({...f, value:e.target.value}))}
                  placeholder="Internal value stored in database"/>
                <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
                  Usually same as label. Can differ for legacy compatibility.
                </p>
              </div>
              <div>
                <label className="label">Sort Order</label>
                <input type="number" className="input w-24" value={addForm.sort_order}
                  onChange={e => setAddForm(f=>({...f, sort_order:e.target.value}))}/>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor:'var(--border)' }}>
              <button onClick={() => setAddModal(null)} className="btn-secondary">Cancel</button>
              <button onClick={handleAdd} disabled={saving || !addForm.label || !addForm.value}
                className="btn-primary flex items-center gap-2">
                {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
                Add Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Item Modal */}
      {editModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setEditModal(null)}/>
          <div className="relative bg-[var(--surface)] rounded-2xl shadow-2xl w-full max-w-md z-10"
            style={{ border:'1px solid var(--border)' }}>
            <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor:'var(--border)' }}>
              <h3 className="font-bold" style={{ color:'var(--text)' }}>Edit Picklist Item</h3>
              <button onClick={() => setEditModal(null)} className="p-1 rounded hover:bg-[var(--surface-alt)]" style={{ color:'var(--text-secondary)' }}><XIcon/></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="label">Label <span className="text-red-500">*</span></label>
                <input className="input" value={editForm.label||''} onChange={e => setEditForm(f=>({...f,label:e.target.value}))}/>
              </div>
              <div>
                <label className="label">Value <span className="text-red-500">*</span></label>
                <input className="input" value={editForm.value||''} onChange={e => setEditForm(f=>({...f,value:e.target.value}))}/>
              </div>
              <div>
                <label className="label">Sort Order</label>
                <input type="number" className="input w-24" value={editForm.sort_order||0}
                  onChange={e => setEditForm(f=>({...f,sort_order:parseInt(e.target.value)||0}))}/>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editForm.is_active !== false}
                  onChange={e => setEditForm(f=>({...f,is_active:e.target.checked}))}
                  className="w-4 h-4 rounded"/>
                <span className="text-sm" style={{ color:'var(--text)' }}>Active (visible in dropdowns)</span>
              </label>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor:'var(--border)' }}>
              <button onClick={() => setEditModal(null)} className="btn-secondary">Cancel</button>
              <button onClick={handleEdit} disabled={saving || !editForm.label || !editForm.value}
                className="btn-primary flex items-center gap-2">
                {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
